import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useResolvedPath } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { Button, Header, IconHighlight, Pagination } from '@aimsos/ui';
import { useMediaQuery, useStickyScroll } from '@aimsos/hooks';

import { AccessDeniedState, EmptyState, ErrorState, NotFoundState } from '../components/states/index.js';
import EntityListSkeleton from '../components/EntityListSkeleton.jsx';
import DataListFilterBar from '../components/DataListFilterBar.jsx';
import DataListFilterSlideout from '../components/DataListFilterSlideout.jsx';
import EntityRowCard from '../components/EntityRowCard.jsx';
import RecordTypeRail, { ALL_RECORD_TYPES_ID } from '../components/RecordTypeRail.jsx';
import { RECORDS_SORT_DIRECTION, RECORDS_SORT_FIELD } from '../constants/dataListFilters.js';
import { isCreatableType } from '../constants/createRecordFields.js';
import { useEntityList } from '../hooks/useEntityList.js';
import { useDataListFilters } from '../hooks/useDataListFilters.js';
import { useProfileTypes } from '../hooks/useProfileTypes.js';

import { IdCard, Inbox, Plus, SearchX } from 'lucide-react';

/** Enough for the rail to list every type in an ordinary tenant without paging. */
const RAIL_TYPES_PAGE_SIZE = 50;

const PAGE_SIZE_OPTIONS = [10, 15, 20, 50];

/**
 * The rail is 200px wide and the gap to the results is 24px. The toolbar sits
 * over the results column only, so on wide screens it is pushed in by the same
 * 224px; below `lg` the rail stacks above and nothing is offset.
 */
const RAIL_AND_RESULTS = 'lg:grid-cols-[200px_minmax(0,1fr)]';
const TOOLBAR_OVER_RESULTS = 'lg:ml-[224px]';
// Collapsed, the rail is one icon button wide (40px) plus the same 24px gap.
const RAIL_AND_RESULTS_COLLAPSED = 'lg:grid-cols-[40px_minmax(0,1fr)]';
const TOOLBAR_OVER_RESULTS_COLLAPSED = 'lg:ml-[64px]';

/**
 * The rail starts level with the toolbar, not under it: the toolbar only spans
 * the results column, so without this the area to its left stays empty. The
 * header wrapper ignores the pointer outside its content, and sits above the
 * rail (z-20 over z-10), so the pulled-up rail stays clickable.
 */
const RAIL_BESIDE_TOOLBAR = 'relative z-10 lg:-mt-16';

const RAIL_COLLAPSED_KEY = 'unified-profiles:rail-collapsed';

/** Tailwind's `lg`: below it the rail stacks above the results and has no toggle. */
const DESKTOP_QUERY = '(min-width: 1024px)';

/** A remembered convenience only: storage may be blocked or absent. */
const readRailCollapsed = () => {
  try {
    return window.localStorage.getItem(RAIL_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * Records: one list for every record type. "All" merges them; the rail on the
 * left narrows to one. The route says which — `/unified-profiles` is All and
 * `/unified-profiles/:entityType` is a single type — so a type stays linkable.
 */
function EntityListPage() {
  const { t } = useTranslation('unified-profiles');
  const { entityType } = useParams();
  const navigate = useNavigate();
  const isAll = !entityType;

  // The domain root, whichever of the two routes this is: `..` from the
  // `:entityType` route and `.` from the index route both resolve to it.
  const { pathname: domainRoot } = useResolvedPath(isAll ? '.' : '..');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [railCollapsed, setRailCollapsed] = useState(readRailCollapsed);
  // The choice is remembered, but only honoured where the toggle exists: the
  // collapse button is hidden below `lg`, so a desktop collapse that followed the
  // user onto a phone would leave unlabeled icons and no way to expand them.
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const isRailCollapsed = railCollapsed && isDesktop;
  const toggleRailCollapsed = useCallback(() => {
    setRailCollapsed((prev) => {
      try {
        window.localStorage.setItem(RAIL_COLLAPSED_KEY, String(!prev));
      } catch {
        /* the panel still collapses for this visit */
      }
      return !prev;
    });
  }, []);

  const scrollRef = useRef(null);
  const { isScrolled, filtersVisible, onMouseEnter, onMouseLeave } = useStickyScroll(scrollRef);
  const showToolbar = !isScrolled || filtersVisible;

  const { types, loading: typesLoading } = useProfileTypes({ pageSize: RAIL_TYPES_PAGE_SIZE });
  const typeLabels = useMemo(
    () => new Map(types.map((type) => [type.id, type.label])),
    [types],
  );

  const filtersState = useDataListFilters({
    defaultSortField: RECORDS_SORT_FIELD,
    defaultSortDirection: RECORDS_SORT_DIRECTION,
  });
  const [isAllFiltersOpen, setIsAllFiltersOpen] = useState(false);
  const openAllFilters = useCallback(() => {
    filtersState.openDraft();
    setIsAllFiltersOpen(true);
  }, [filtersState]);
  const closeAllFilters = useCallback(() => setIsAllFiltersOpen(false), []);
  const { filters, search, orderBy, orderDirection, hasActiveQuery, clearAll, resetFilters } =
    filtersState;

  const { items, filterDefs, sortDefs, meta, loading, error, accessDenied, notFound, refetch } =
    useEntityList({
      entityType,
      page,
      pageSize,
      orderBy,
      orderDirection,
      filters,
      search,
    });

  // Narrowing the list can leave the page the user was on past the end, and
  // switching type starts a different list altogether. Both go back to page 1.
  const queryKey = `${entityType ?? ALL_RECORD_TYPES_ID}|${orderBy}|${orderDirection}|${JSON.stringify(
    filters ?? {},
  )}|${search?.query ?? ''}`;
  const [lastQueryKey, setLastQueryKey] = useState(queryKey);
  if (lastQueryKey !== queryKey) {
    setLastQueryKey(queryKey);
    if (page !== 1) setPage(1);
  }

  // A row knows its own type, so a record opened from the All view lands on
  // its own profile. A payload that does not say falls back to the type asked
  // for; under All there is no such type, so an untagged row has nowhere to go.
  const handleSelect = useCallback(
    (entity) => {
      const type = entity.entityType ?? entityType;
      if (!type) return;
      navigate(`${domainRoot}/${type}/${entity.id}`);
    },
    [navigate, domainRoot, entityType],
  );
  const handlePrevPage = useCallback(() => setPage((p) => Math.max(1, p - 1)), []);
  const handleNextPage = useCallback(() => setPage((p) => p + 1), []);
  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size);
    setPage(1);
  }, []);
  // New record opens on the type being looked at when that type can be created;
  // on All, or on a type like Employees, it opens on the default.
  const createRecordPath = `${domainRoot}/new${isCreatableType(entityType) ? `?type=${entityType}` : ''}`;
  const handleCreateRecord = useCallback(() => navigate(createRecordPath), [navigate, createRecordPath]);
  const primaryCta = useMemo(
    () => ({ label: t('create.cta'), onClick: handleCreateRecord, leftIcon: Plus, size: 'md' }),
    [t, handleCreateRecord],
  );

  const handleBackToRecords = useCallback(() => navigate(domainRoot), [navigate, domainRoot]);

  // Search and sort mean the same on any type and travel with the user; chosen
  // filters do not — their ids belong to a type and would match nothing on another.
  // Choosing the type that is already selected changes nothing, so it must not
  // throw away the filters the user has set on it.
  const selectedTypeId = entityType ?? ALL_RECORD_TYPES_ID;
  const handleSelectType = useCallback(
    (id) => {
      if (id === selectedTypeId) return;
      resetFilters();
      navigate(id === ALL_RECORD_TYPES_ID ? domainRoot : `${domainRoot}/${id}`);
    },
    [navigate, domainRoot, resetFilters, selectedTypeId],
  );

  const typeLabel = isAll ? null : (typeLabels.get(entityType) ?? entityType);

  const rangeLabel = useMemo(() => {
    if (meta.totalItems === 0 || items.length === 0) return '0-0';
    const start = (meta.currentPage - 1) * meta.perPage + 1;
    return `${start}-${start + items.length - 1}`;
  }, [meta.currentPage, meta.perPage, meta.totalItems, items.length]);

  const paginationLabels = useMemo(
    () => ({
      rowsPerPage: t('list.pagination.rowsPerPage'),
      items: t('list.pagination.items', { range: rangeLabel, total: meta.totalItems }),
      previousPage: t('list.pagination.previousPage'),
      nextPage: t('list.pagination.nextPage'),
    }),
    [rangeLabel, t, meta.totalItems],
  );

  const results = () => {
    if (loading) return <EntityListSkeleton perPage={pageSize} />;
    if (accessDenied) return <AccessDeniedState onBack={handleBackToRecords} />;
    if (notFound) return <NotFoundState onBack={handleBackToRecords} />;
    if (error) return <ErrorState message={error} onRetry={refetch} />;
    if (items.length === 0) {
      return hasActiveQuery ? (
        <EmptyState
          testId="no-results-state"
          icon={SearchX}
          title={t('list.noResults.title')}
          subtitle={t('list.noResults.subtitle')}
          action={
            <Button variant="secondary" size="small" onClick={clearAll}>
              {t('list.filters.clearAll')}
            </Button>
          }
        />
      ) : (
        <EmptyState
          icon={Inbox}
          title={t('list.empty.title')}
          subtitle={
            isAll
              ? t('list.empty.subtitleAll')
              : t('list.empty.subtitle', { type: typeLabel })
          }
        />
      );
    }

    return (
      <div className="flex flex-col gap-3">
        {items.map((entity) => (
          <EntityRowCard
            key={`${entity.entityType ?? entityType}:${entity.id}`}
            entity={entity}
            // Under All a row needs to say what it is; a type the payload
            // already tags keeps its own tag.
            typeLabel={isAll ? typeLabels.get(entity.entityType) : undefined}
            onSelect={handleSelect}
          />
        ))}
      </div>
    );
  };

  const showPagination = !error && !accessDenied && !notFound && (meta.totalItems > 0 || loading);

  // The header only invites the user to open a record while there is one to
  // open. Caught in the browser: it kept saying "select a record" over the empty
  // state and over access-denied.
  const hasSelectableRecords = !accessDenied && !notFound && !error && (loading || items.length > 0);

  return (
    <div data-testid="entity-list" className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div ref={scrollRef} className="scrollbar-aimsos-auto min-h-0 flex-1 overflow-y-auto">
        <div className="flex min-h-full flex-col">
          {/* Hover handlers only while compressed: at rest the toolbar is
              already there, and arming them would schedule work for every
              pointer that merely crosses the header. */}
          <div
            className="pointer-events-none sticky top-0 z-20 px-6"
            onMouseEnter={isScrolled ? onMouseEnter : undefined}
            onMouseLeave={isScrolled ? onMouseLeave : undefined}
          >
            {/* Vertical padding belongs on the PAINTED element, never on the
                sticky wrapper — outside it the backdrop stops at the header's
                own box and rows scroll visibly through the gutter. */}
            <div
              className={['pb-6 pt-6 transition-colors duration-200', isScrolled ? 'bg-surface-base' : '']
                .filter(Boolean)
                .join(' ')}
            >
              <div className="pointer-events-auto">
                <Header
                  size={isScrolled ? 'compress' : 'M'}
                  showIcon
                  icon={<IconHighlight icon={IdCard} bgColor="Primary" iconColor="PrimaryAlt" size="small" />}
                  title={t('list.title')}
                  showDescription={!isScrolled && hasSelectableRecords}
                  description={t('list.description')}
                  primaryCta={primaryCta}
                />
              </div>

              {/* Collapsed by CSS rather than unmounted, and `inert` keeps the
                  hidden toolbar out of the tab order. `overflow` is hidden ONLY
                  while collapsed: kept on permanently it would clip any popover
                  rendered in here. */}
              <div
                inert={showToolbar ? undefined : ''}
                className={[
                  'transition-all duration-200',
                  'pointer-events-auto',
                  isRailCollapsed ? TOOLBAR_OVER_RESULTS_COLLAPSED : TOOLBAR_OVER_RESULTS,
                  // The 16px under the title is PADDING of this block, not a margin
                  // outside it: the header wrapper ignores the pointer, so a margin
                  // is a hole in the hover area and the pointer leaving through it
                  // hides the toolbar before it can reach it.
                  showToolbar
                    ? 'pt-4 max-h-40 overflow-visible opacity-100'
                    : 'pt-0 max-h-0 overflow-hidden opacity-0',
                ].join(' ')}
                data-testid="entity-list-toolbar"
              >
                <DataListFilterBar
                  filtersState={filtersState}
                  filterDefs={filterDefs}
                  sortDefs={sortDefs}
                  onAllFiltersClick={openAllFilters}
                />
              </div>
            </div>

            {/* Rows have to leave somewhere. Without this they reappear at full
                opacity a pixel under a hard edge and the bar reads as clipping
                the list rather than floating over it. */}
            {isScrolled ? (
              <div
                aria-hidden
                className="pointer-events-none h-10 bg-gradient-to-b from-surface-base to-transparent"
              />
            ) : null}
          </div>

          {/* No bottom padding while the pagination is showing: padding here reserves
              space BELOW the sticky bar, and a sticky element only stays pinned while its
              flow position is off screen. Reaching the end of the scroll drops it into
              that position — one `pb-6` above the bottom edge — so the bar jumped up 24px
              on the last scroll step. The breathing room rides on the bar instead. */}
          <div
            className={['flex min-w-0 flex-1 flex-col gap-4 px-6', showPagination ? '' : 'pb-6']
              .filter(Boolean)
              .join(' ')}
          >
            <div className={['grid min-w-0 gap-6', isRailCollapsed ? RAIL_AND_RESULTS_COLLAPSED : RAIL_AND_RESULTS].join(' ')}>
              <RecordTypeRail
                types={types}
                selectedId={selectedTypeId}
                onSelect={handleSelectType}
                loading={typesLoading}
                collapsed={isRailCollapsed}
                className={RAIL_BESIDE_TOOLBAR}
                onToggleCollapsed={toggleRailCollapsed}
              />
              <div className="min-w-0">{results()}</div>
            </div>

            {showPagination ? (
              /* The breathing room goes on a WRAPPER, not through `Pagination`'s
                 `className`: that prop is appended to the bar's own painted box
                 (`BASE_CLASS`, `px-3 py-2 rounded-xl border`), so a `pb-6` there
                 lands *inside* the pill — 8px above, 24px below, and 16px taller
                 than the identical bar on Widget Library. It sits outside the
                 grid so the bar spans the rail too, as in the design. */
              <div className="sticky bottom-0 z-10 mt-auto bg-surface-base pb-6">
                <Pagination
                  rangeLabel={rangeLabel}
                  totalItems={meta.totalItems}
                  currentPage={meta.currentPage}
                  totalPages={meta.totalPages}
                  hasPrevious={meta.hasPrevious}
                  hasNext={meta.hasNext}
                  perPage={pageSize}
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  onPrevPage={handlePrevPage}
                  onNextPage={handleNextPage}
                  onPageSizeChange={handlePageSizeChange}
                  disabled={loading}
                  ariaLabelPerPage={t('list.pagination.rowsPerPage')}
                  labels={paginationLabels}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <DataListFilterSlideout
        isOpen={isAllFiltersOpen}
        onClose={closeAllFilters}
        filtersState={filtersState}
        filterDefs={filterDefs}
        sortDefs={sortDefs}
      />
    </div>
  );
}

export default EntityListPage;
